import React, { useState, useRef } from "react";
import { apiRequest } from "../utils/ApiRequest";
import "./LasgidiGotTalent.css";

const LasgidiGotTalent = () => {
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    over18: null,
    guardianName: "",
    guardianContact: "",
    confirmAge: false,
    videoFile: null,
    videoLink: "",
    talentType: "",
    otherTalent: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const showGuardianField = formData.over18 === false;

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (type === "file") {
      const file = files[0];
      if (!file) return;

      const maxSize = 100 * 1024 * 1024; // 100MB
      if (file.size > maxSize) {
        alert(
          "The selected file exceeds 100MB. Please upload a smaller video."
        );
        e.target.value = "";
        setFormData((prev) => ({ ...prev, videoFile: null }));
        return;
      }

      setFormData((prev) => ({ ...prev, videoFile: file }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleTalentChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      talentType: value,
      otherTalent: value !== "Other" ? "" : prev.otherTalent,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // --- Validation ---
    if (
      !formData.name.trim() ||
      !formData.email.trim() ||
      !formData.phone.trim()
    ) {
      setError("Name, email, and phone are required.");
      return;
    }

    if (
      !formData.over18 &&
      (!formData.guardianName.trim() || !formData.guardianContact.trim())
    ) {
      setError("Please provide guardian information.");
      return;
    }

    if (!formData.confirmAge) {
      setError("Please confirm that the information provided is accurate.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      let uploadedVideoUrl = "";

      // --- Upload video first if provided ---
      if (formData.videoFile) {
        const videoForm = new FormData();
        videoForm.append("videoFile", formData.videoFile);

        const uploadResult = await apiRequest({
          script: "upload-video",
          data: videoForm,
          isFormData: true,
        });

        if (uploadResult.success) {
          uploadedVideoUrl = uploadResult.url;
        } else {
          throw new Error(uploadResult.message || "Video upload failed.");
        }
      }

      // --- Prepare main submission ---
      const submissionData = new FormData();
      submissionData.append("name", formData.name.trim());
      submissionData.append("email", formData.email.trim());
      submissionData.append("phone", formData.phone.trim());
      const talentValue =
        formData.talentType === "Other"
          ? formData.otherTalent.trim()
          : formData.talentType;
      submissionData.append("talentType", talentValue);
      submissionData.append("over18", formData.over18 ? "true" : "false");
      submissionData.append("confirmAge", formData.confirmAge ? "1" : "");
      submissionData.append("guardianName", formData.guardianName.trim());
      submissionData.append("guardianContact", formData.guardianContact.trim());
      submissionData.append("videoFile", uploadedVideoUrl);
      submissionData.append("videoLink", formData.videoLink.trim());

    

      const result = await apiRequest({
        script: "lasgidi-got-talent",
        data: submissionData,
        isFormData: true,
      });

      if (result.success) {
        alert("Application submitted successfully!");
        setFormData({
          name: "",
          email: "",
          phone: "",
          over18: null,
          guardianName: "",
          guardianContact: "",
          confirmAge: false,
          videoFile: null,
          videoLink: "",
          talentType: "",
          otherTalent: "",
        });
        if (fileInputRef.current) fileInputRef.current.value = "";
      } else {
        setError(result.message || "Submission failed.");
      }
    } catch (err) {
      console.error("Submission Error:", err);
      setError(err.message || "An error occurred while submitting the form.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="talent-page page-container">
      {loading && (
        <div className="spinner-overlay">
          <div className="spinner"></div>
        </div>
      )}

      <form
        className="talent-form"
        onSubmit={handleSubmit}
        encType="multipart/form-data"
      >
        <div className="talent-page-header">
          <h1>Lasgidi Got Talent: Registration</h1>
          <p>
            Fill out the form below to register your interest before the event
            date is announced.
          </p>
        </div>

        {error && <p className="auth-error">{error}</p>}

        <label>Name:</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
        />

        <label>Email:</label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
        />

        <label>Phone:</label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          required
        />

        <label>What talent will you display?</label>
        <select
          name="talentType"
          value={formData.talentType}
          onChange={handleTalentChange}
          required
        >
          <option value="" disabled>
            Select your talent
          </option>
          <option value="Singing">Singing</option>
          <option value="Dance">Dance</option>
          <option value="Poetry / Spoken Word">Poetry / Spoken Word</option>
          <option value="Comedy">Comedy</option>
          <option value="Acting">Acting</option>
          <option value="Instrumental Performance">
            Instrumental Performance
          </option>
          <option value="Art">Art</option>
          <option value="Other">Other</option>
        </select>

        {formData.talentType === "Other" && (
          <>
            <label>Please specify your talent:</label>
            <input
              type="text"
              name="otherTalent"
              value={formData.otherTalent}
              onChange={handleChange}
              required
            />
          </>
        )}

        <label>Upload Video Clip (100MB max) or YouTube Link:</label>
        <input
          type="file"
          name="videoFile"
          accept="video/*"
          onChange={handleChange}
          ref={fileInputRef}
        />
        <input
          type="url"
          name="videoLink"
          value={formData.videoLink}
          placeholder="YouTube Link"
          onChange={handleChange}
        />

        <label className="question-label">Are you 18 years or older?</label>
        <div className="age-toggle">
          <input
            type="radio"
            id="ageYes"
            name="over18"
            checked={formData.over18 === true}
            onChange={() =>
              setFormData({
                ...formData,
                over18: true,
                guardianName: "",
                guardianContact: "",
              })
            }
          />
          <label htmlFor="ageYes">Yes</label>

          <input
            type="radio"
            id="ageNo"
            name="over18"
            checked={formData.over18 === false}
            onChange={() => setFormData({ ...formData, over18: false })}
          />
          <label htmlFor="ageNo">No</label>
        </div>

        {showGuardianField && (
          <>
            <label>Parent/Guardian Name:</label>
            <input
              type="text"
              name="guardianName"
              value={formData.guardianName}
              onChange={handleChange}
              required
            />

            <label>Parent/Guardian Contact:</label>
            <input
              type="text"
              name="guardianContact"
              value={formData.guardianContact}
              onChange={handleChange}
              required
            />
          </>
        )}

        <label>
          <input
            type="checkbox"
            name="confirmAge"
            checked={formData.confirmAge}
            onChange={handleChange}
          />
          I confirm that the information provided is accurate.
        </label>

        <button
          type="submit"
          className="post-review-btn lgt-submit-btn"
          disabled={loading}
        >
          {loading ? <span className="spinner"></span> : "Submit Application"}
        </button>
      </form>
    </div>
  );
};

export default LasgidiGotTalent;
